import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { getSitePrices } from "@/lib/pricing.functions";
import {
  formatDualPrice,
  SINGLE_LESSONS,
  PACKAGE_LESSONS,
  RANGE_BUCKET_PRICES,
  GIFT_CARD_STARTING,
} from "@/lib/marketing-pricing";
import { SITE_PHONE, SITE_PHONE_TEL } from "@/lib/site-info";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import { pageHead } from "@/lib/seo";
import { localizePriceLabel } from "@/lib/localize-price-label";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/prices")({
  head: () =>
    pageHead({
      title: m.prices_meta_title(),
      description: m.prices_meta_description(),
      path: "/prices",
    }),
  component: PricesPage,
});

function PriceList({
  items,
}: {
  items: { label: string; cashCents: number; cardCents: number }[];
}) {
  return (
    <ul className="divide-y divide-white/10">
      {items.map((item) => (
        <li
          key={item.label}
          className="flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <span className="text-sm text-foreground/85">{localizePriceLabel(item.label)}</span>
          <span className="font-display text-lg text-ember">
            {formatDualPrice(item.cashCents, item.cardCents)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PricesPage() {
  const pricesQ = useQuery({ queryKey: ["site-prices"], queryFn: () => getSitePrices() });

  const fromDb = pricesQ.data ?? [];
  const useDb = fromDb.length > 0;

  const singleLessons = useDb
    ? fromDb
        .filter((p) => p.category === "lesson_single")
        .map((p) => ({ label: p.label, cashCents: p.cash_cents, cardCents: p.card_cents }))
    : SINGLE_LESSONS;

  const seriesLessons = useDb
    ? fromDb
        .filter((p) => p.category === "lesson_package")
        .map((p) => ({ label: p.label, cashCents: p.cash_cents, cardCents: p.card_cents }))
    : PACKAGE_LESSONS;

  const rangeBuckets = useDb
    ? fromDb
        .filter((p) => p.category === "range_bucket")
        .map((p) => ({ label: p.label, cashCents: p.cash_cents, cardCents: p.card_cents }))
    : RANGE_BUCKET_PRICES;

  const giftCardStarting = rangeBuckets[0] ?? GIFT_CARD_STARTING;

  return (
    <>
      <section className="bg-ink">
        <div className="container-page page-hero-pad">
          <span className="eyebrow">{m.nav_prices()}</span>
          <h1 className="mt-3 font-display text-hero leading-[0.95] md:text-8xl">
            <EmphasizedTitle lead={m.prices_title_lead()} accent={m.prices_title_accent()} />
          </h1>
          <p className="mt-5 max-w-xl text-foreground/80">{m.prices_intro()}</p>
        </div>
      </section>

      <section className="container-page page-follow-pad space-y-20">
        <div>
          <span className="eyebrow">{m.prices_lessons_eyebrow()}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">{m.prices_individual()}</h2>
          <p className="mt-2 text-sm text-foreground/60">
            {m.prices_starting_at({
              price: formatDualPrice(
                singleLessons[0]?.cashCents ?? 7000,
                singleLessons[0]?.cardCents ?? 7280,
              ),
            })}
          </p>
          <div className="mt-8 border border-white/10 bg-black/40 p-6 md:p-8">
            <PriceList items={singleLessons} />
          </div>
          <div className="mt-10">
            <h3 className="font-display text-2xl">{m.prices_series()}</h3>
            <div className="mt-6 border border-white/10 bg-black/40 p-6 md:p-8">
              <PriceList items={seriesLessons} />
            </div>
            <p className="mt-4 text-sm text-foreground/60">{m.prices_package_hint()}</p>
          </div>
          <Link to="/schedule" className="btn-ember btn-ember-hover mt-8 inline-flex">
            {m.prices_book_lessons()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </div>

        <div>
          <span className="eyebrow">{m.nav_range()}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">{m.prices_range_buckets()}</h2>
          <p className="mt-2 text-sm text-foreground/60">
            {m.prices_starting_at({
              price: formatDualPrice(giftCardStarting.cashCents, giftCardStarting.cardCents),
            })}
          </p>
          <div className="mt-8 border border-white/10 bg-black/40 p-6 md:p-8">
            <PriceList items={rangeBuckets} />
          </div>
        </div>

        <div>
          <span className="eyebrow">{m.prices_gifts_eyebrow()}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">{m.prices_gift_cards()}</h2>
          <p className="mt-2 text-sm text-foreground/60">
            {m.prices_starting_at({
              price: formatDualPrice(giftCardStarting.cashCents, giftCardStarting.cardCents),
            })}
          </p>
          <div className="mt-8 border border-white/10 bg-ink p-9">
            <ul className="space-y-2 text-sm text-foreground/85">
              <li>{m.prices_gift_buy()}</li>
              <li>{m.prices_gift_contact()}</li>
            </ul>
            <a href={SITE_PHONE_TEL} className="btn-ember btn-ember-hover mt-6 inline-flex">
              {m.prices_call({ phone: SITE_PHONE })}
            </a>
          </div>
        </div>

        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {m.prices_cash_card()}
        </p>
      </section>
    </>
  );
}
